/* eslint-disable camelcase */

import { useEffect, useState } from "react";
import { useLocalization } from "@fluent/react";
import { entities } from "byzantine";
import {
  Button,
  Chip,
  ContentCard,
  LoadingSkeleton,
  MenuButton,
  Row,
  Table,
} from "@narmi/design_system";
import { useUserFeatures } from "../../contexts/UserFeaturesContext";
import RecipientsGrid from "./RecipientsGrid";
import RecipientsTable from "./RecipientsTable";
import SettingsRecipientDrawer from "./SettingsRecipientDrawer";

import type { RecipientsView } from "./RecipientsTable";

type WireRecipientFilter = "active" | "archived";

interface WireRecipientCardProps {
  recipient: API.Recipient;
  view: RecipientsView;
  onArchive?: (recipient: API.Recipient) => void;
}

// renders the recipient as a card, or as a table row in list view
const WireRecipientCard = ({
  recipient,
  view,
  onArchive,
}: WireRecipientCardProps) => {
  const { l10n } = useLocalization();
  const features = useUserFeatures();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { name, nickname } = recipient;
  const wireMethods = entities.recipients.getRecipientWireMethods(recipient, {
    hasInternationalWires: Boolean(
      features.international_wires || features.international_wires_consumer
    ),
  });
  const isDomesticWire = wireMethods.includes("wire");
  const isInternationalWire = wireMethods.includes("internationalWire");

  const toggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  const actionsMenu = (
    <MenuButton
      triggerIcon={view === "list" ? "more-vertical" : "more-horizontal"}
      label={l10n.getString("wire-recipient-actions-label", { name })}
    >
      {/* the drawer has no separate view mode, so View and Edit both open it */}
      <MenuButton.Item
        label={l10n.getString("label-view")}
        startIcon="eye"
        onSelect={toggleDrawer}
      />
      <MenuButton.Item
        label={l10n.getString("button-menu-edit")}
        startIcon="edit-2"
        onSelect={toggleDrawer}
      />
      <MenuButton.Item
        label={l10n.getString("label-archive")}
        startIcon="archive"
        onSelect={() => onArchive?.(recipient)}
      />
    </MenuButton>
  );

  const wireMethodChips = (
    <>
      {isInternationalWire ? (
        <Row.Item shrink>
          <Chip kind="info" label={l10n.getString("label-intl-wire")} />
        </Row.Item>
      ) : null}
      {isDomesticWire ? (
        <Row.Item shrink>
          <Chip kind="info" label={l10n.getString("label-domestic-wire")} />
        </Row.Item>
      ) : null}
    </>
  );

  return (
    <>
      {view === "list" ? (
        <Table.Row>
          <Table.Cell>
            <div>
              <div className="fontColor--heading">{name}</div>
              {nickname ? (
                <div className="fontSize--xs fontColor--secondary">
                  {nickname}
                </div>
              ) : null}
            </div>
          </Table.Cell>
          <Table.Cell>
            <Row gapSize="s">{wireMethodChips}</Row>
          </Table.Cell>
          <Table.Cell>
            <Row justifyContent="center">{actionsMenu}</Row>
          </Table.Cell>
        </Table.Row>
      ) : (
        <ContentCard kind="elevated">
          <div className="recipient-section-header">
            <div
              className="fontColor--heading fontWeight--semibold"
              style={{ overflowWrap: "anywhere" }}
            >
              {name}
            </div>
            <div style={{ marginTop: "-4px" }}>{actionsMenu}</div>
          </div>
          <div className="fontSize--xs fontColor--secondary margin--top--xxs wire-recipient-nickname">
            {nickname}
          </div>
          <Row gapSize="s" className="margin--top--m">
            {wireMethodChips}
          </Row>
        </ContentCard>
      )}
      <SettingsRecipientDrawer
        isOpen={isDrawerOpen}
        recipientId={recipient.id}
        onClose={toggleDrawer}
      />
    </>
  );
};

interface WireRecipientSectionProps {
  // there is no archive API yet: callers pass the archived recipients and the
  // archive handler once one exists. Until then, Archived shows the empty state
  archivedRecipients?: API.Recipient[];
  onArchive?: (recipient: API.Recipient) => void;
  view?: RecipientsView;
  isCollapsible?: boolean;
}

const WireRecipientSection = ({
  archivedRecipients = [],
  onArchive,
  view = "grid",
  isCollapsible = false,
}: WireRecipientSectionProps) => {
  const { l10n } = useLocalization();
  const features = useUserFeatures();
  const [isAddDrawerOpen, setIsAddDrawerOpen] = useState(false);
  const [filter, setFilter] = useState<WireRecipientFilter>("active");
  const { send: fetchRecipients, loading } =
    entities.recipients.useFetchRecipients();
  const recipients = entities.recipients.useRecipients();
  const hasInternationalWiresFeature = Boolean(
    features.international_wires || features.international_wires_consumer
  );

  useEffect(() => {
    fetchRecipients();
  }, []);

  // the section lists recipients that have at least one wire method the user can send
  const activeWireRecipients = (recipients ?? []).filter(
    (recipient) =>
      entities.recipients.getRecipientWireMethods(recipient, {
        hasInternationalWires: hasInternationalWiresFeature,
      }).length > 0
  );
  const visibleRecipients =
    filter === "active" ? activeWireRecipients : archivedRecipients;

  const toggleAddDrawer = () => {
    setIsAddDrawerOpen(!isAddDrawerOpen);
  };

  // the selected filter is a chip, the other a text button to switch to it
  const renderFilter = (value: WireRecipientFilter, label: string) => (
    <Row.Item shrink>
      {filter === value ? (
        <Chip kind="primary" label={label} />
      ) : (
        <Button
          kind="plain"
          size="xs"
          label={label}
          onClick={() => setFilter(value)}
        />
      )}
    </Row.Item>
  );

  return (
    <>
      <div className="margin--top--xl margin--bottom--l">
        <Row alignItems="center">
          <Row.Item>
            <Row alignItems="center" gapSize="m">
              <Row.Item shrink>
                <div className="fontSize--l fontColor--heading fontWeight--bold">
                  {l10n.getString("heading-wire-recipients")}
                </div>
              </Row.Item>
              <Row.Item shrink>
                <Row alignItems="center" gapSize="s">
                  {renderFilter(
                    "active",
                    l10n.getString("label-recipients-filter-active")
                  )}
                  {renderFilter(
                    "archived",
                    l10n.getString("label-recipients-filter-archived")
                  )}
                </Row>
              </Row.Item>
            </Row>
          </Row.Item>
          <Row.Item shrink>
            <Button
              kind="plain"
              size="m"
              startIcon="plus"
              label={l10n.getString("recipients-add-recipient-button")}
              onClick={toggleAddDrawer}
            />
          </Row.Item>
        </Row>
      </div>
      <LoadingSkeleton content="paragraph" isLoading={loading}>
        {view === "list" && visibleRecipients.length ? (
          <RecipientsTable
            items={visibleRecipients}
            columns={[
              {
                label: l10n.getString("label-name"),
                getSortValue: (recipient) => recipient.name,
              },
              { label: l10n.getString("label-recipients-payment-method") },
            ]}
            renderRow={(recipient) => (
              <WireRecipientCard
                key={recipient.id}
                recipient={recipient}
                view={view}
                onArchive={onArchive}
              />
            )}
          />
        ) : (
          <RecipientsGrid
            items={visibleRecipients}
            renderItem={(recipient) => (
              <WireRecipientCard
                key={recipient.id}
                recipient={recipient}
                view={view}
                onArchive={onArchive}
              />
            )}
            emptyMessage={
              filter === "active"
                ? l10n.getString("message-no-wire-recipients")
                : l10n.getString("message-no-archived-wire-recipients")
            }
            isCollapsible={isCollapsible}
          />
        )}
      </LoadingSkeleton>
      <SettingsRecipientDrawer
        isOpen={isAddDrawerOpen}
        onClose={toggleAddDrawer}
      />
    </>
  );
};

export default WireRecipientSection;
